import { type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

type Variant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'gold' | 'outline';

interface Props extends HTMLAttributes<HTMLSpanElement> {
  variant?: Variant;
}

// Neutral default uses tokens (adapts to dark automatically). Status variants keep their semantic
// hue but pair every light tint with a dark-mode equivalent so they never render a light chip on a
// dark surface (translucent -500/10 fill + -300 text + -500/30 border reads correctly in both).
const VARIANTS: Record<Variant, string> = {
  default: 'bg-muted text-muted-foreground border border-border',
  success: 'bg-emerald-50 text-emerald-700 border border-emerald-200/60 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30',
  warning: 'bg-amber-50 text-amber-700 border border-amber-200/60 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30',
  danger:  'bg-red-50 text-red-700 border border-red-200/60 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/30',
  info:    'bg-blue-50 text-blue-700 border border-blue-200/60 dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/30',
  // Gold = amber/ochre — used for listed hearings, time-sensitive matters
  gold:    'bg-amber-50 text-amber-800 border border-amber-200/70 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30',
  outline: 'bg-transparent text-muted-foreground border border-border/80',
};

export function Badge({ className, variant = 'default', ...props }: Props) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold leading-tight tracking-[0.02em]',
        VARIANTS[variant],
        className,
      )}
      {...props}
    />
  );
}
